import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useOrbitAuth } from '../context/OrbitAuthContext';
import OrbitLayout from '../components/OrbitLayout';
import {
  Storefront, MapPin, Tag, CurrencyDollar, Check,
  ArrowRight, ArrowLeft, SpinnerGap, CheckCircle,
  Globe, Phone as PhoneIcon, Envelope, Info
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  {
    key: 'free', price: '$0',
    name_fr: 'Gratuit', name_en: 'Free',
    desc_fr: 'Fiche de base dans le repertoire', desc_en: 'Basic directory listing',
    features_fr: ['Inscription de base', '1 photo', 'Formulaire de contact'],
    features_en: ['Basic listing', '1 photo', 'Contact form'],
  },
  {
    key: 'silver', price: '$29',
    name_fr: 'Argent', name_en: 'Silver',
    desc_fr: 'Fiche amelioree avec visibilite accrue', desc_en: 'Enhanced listing with more visibility',
    features_fr: ['5 photos', 'Telephone & email visibles', 'Analytiques de base'],
    features_en: ['5 photos', 'Phone & email visible', 'Basic analytics'],
  },
  {
    key: 'gold', price: '$59',
    name_fr: 'Or', name_en: 'Gold',
    desc_fr: 'Placement prioritaire et badge verifie', desc_en: 'Priority placement and verified badge',
    features_fr: ['15 photos', 'Badge verifie', 'En vedette', 'Notifications prospects'],
    features_en: ['15 photos', 'Verified badge', 'Featured listing', 'Lead notifications'],
    popular: true,
  },
  {
    key: 'platinum', price: '$99',
    name_fr: 'Platine', name_en: 'Platinum',
    desc_fr: 'Visibilite maximale et gestionnaire dedie', desc_en: 'Maximum visibility and dedicated manager',
    features_fr: ['Photos illimitees', 'Banniere vedette', 'Placement #1', 'Gestionnaire dedie'],
    features_en: ['Unlimited photos', 'Featured banner', '#1 Placement', 'Dedicated manager'],
  },
];

export default function BusinessRegisterPage() {
  const { language } = useLanguage();
  const { token } = useOrbitAuth();
  const navigate = useNavigate();
  const t = (fr, en) => language === 'fr' ? fr : en;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [existingBiz, setExistingBiz] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  // Form state
  const [form, setForm] = useState({
    name: '', category: '', subcategory: '',
    description_fr: '', description_en: '',
    postal_code: '', city: '', neighborhood: '',
    province_code: '', country: 'CA',
    address: '', phone: '', email: '', website: '',
    plan: 'free',
  });

  // Location detection
  const [locLoading, setLocLoading] = useState(false);
  const [locDetected, setLocDetected] = useState(false);

  // Check if user already has a business + load categories
  useEffect(() => {
    const init = async () => {
      try {
        const [bizRes, catRes] = await Promise.all([
          fetch(`${API}/api/orbit/business/my`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/api/directory/categories`),
        ]);
        const bizData = await bizRes.json();
        if (bizData.business) setExistingBiz(bizData.business);
        const catData = await catRes.json();
        setCategories(catData.categories || []);
        setSubcategories(catData.subcategories || []);
      } catch (e) { console.error(e); }
      setCheckingExisting(false);
    };
    if (token) init();
  }, [token]);

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const lookupPostalCode = useCallback(async (code) => {
    if (!code || code.replace(/\s/g, '').length < 3) return;
    setLocLoading(true);
    try {
      const res = await fetch(`${API}/api/locations/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postal_code: code }),
      });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({
          ...prev,
          city: data.city || '',
          neighborhood: data.neighborhood || '',
          province_code: data.province_code || '',
          country: data.country || 'CA',
        }));
        setLocDetected(true);
      }
    } catch { /* silent */ }
    setLocLoading(false);
  }, []);

  const handlePostalChange = (e) => {
    const val = e.target.value.toUpperCase();
    updateForm('postal_code', val);
    const clean = val.replace(/\s/g, '');
    if (clean.length >= 3) lookupPostalCode(val);
    else setLocDetected(false);
  };

  const filteredSubs = subcategories.filter(s => !form.category || s.parent === form.category);

  const canGoNext = () => {
    if (step === 1) return form.name && form.category && form.description_fr && form.description_en;
    if (step === 2) return form.city && form.postal_code;
    if (step === 3) return true;
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/orbit/business/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(t('Entreprise inscrite avec succes !', 'Business registered successfully!'));
        setStep(5); // Confirmation step
      } else {
        const err = await res.json();
        toast.error(err.detail || t('Erreur', 'Error'));
      }
    } catch { toast.error(t('Erreur de connexion', 'Connection error')); }
    setLoading(false);
  };

  if (checkingExisting) {
    return (
      <OrbitLayout>
        <div className="flex items-center justify-center py-32">
          <SpinnerGap className="w-10 h-10 text-[var(--ls-terracotta)] animate-spin" />
        </div>
      </OrbitLayout>
    );
  }

  // Already has a business
  if (existingBiz) {
    return (
      <OrbitLayout>
        <div className="max-w-2xl mx-auto" data-testid="business-existing">
          <div className="bg-white border border-[hsl(40,20%,88%)] rounded-2xl p-8 text-center">
            <Storefront className="w-16 h-16 text-[var(--ls-terracotta)] mx-auto mb-4" weight="fill" />
            <h2 className="text-xl font-bold text-[var(--ls-text)] mb-2">
              {t('Votre entreprise est inscrite', 'Your business is registered')}
            </h2>
            <p className="text-[var(--ls-text-muted)] mb-4">{existingBiz.name}</p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold mb-6"
              style={{
                backgroundColor: existingBiz.status === 'approved' ? '#10B98122' : existingBiz.status === 'rejected' ? '#EF444422' : '#F59E0B22',
                color: existingBiz.status === 'approved' ? '#10B981' : existingBiz.status === 'rejected' ? '#EF4444' : '#F59E0B',
              }}>
              {existingBiz.status === 'approved' && t('Approuvee', 'Approved')}
              {existingBiz.status === 'pending' && t('En attente d\'approbation', 'Pending approval')}
              {existingBiz.status === 'rejected' && t('Refusee', 'Rejected')}
            </div>
            <div className="grid grid-cols-2 gap-3 text-left mb-6">
              <div className="bg-[var(--ls-sand-dark)] rounded-xl p-3">
                <span className="text-xs text-[var(--ls-text-muted)]">{t('Categorie', 'Category')}</span>
                <p className="text-[var(--ls-text)] text-sm font-medium">{existingBiz.category}</p>
              </div>
              <div className="bg-[var(--ls-sand-dark)] rounded-xl p-3">
                <span className="text-xs text-[var(--ls-text-muted)]">{t('Ville', 'City')}</span>
                <p className="text-[var(--ls-text)] text-sm font-medium">{existingBiz.city}{existingBiz.neighborhood ? ` (${existingBiz.neighborhood})` : ''}</p>
              </div>
              <div className="bg-[var(--ls-sand-dark)] rounded-xl p-3">
                <span className="text-xs text-[var(--ls-text-muted)]">{t('Plan', 'Plan')}</span>
                <p className="text-[var(--ls-text)] text-sm font-medium capitalize">{existingBiz.plan}</p>
              </div>
              <div className="bg-[var(--ls-sand-dark)] rounded-xl p-3">
                <span className="text-xs text-[var(--ls-text-muted)]">{t('Statut', 'Status')}</span>
                <p className="text-[var(--ls-text)] text-sm font-medium capitalize">{existingBiz.status}</p>
              </div>
            </div>
            <button onClick={() => navigate('/dashboard')}
              className="px-6 py-2.5 bg-[var(--ls-terracotta)] text-white rounded-full text-sm font-semibold hover:bg-[var(--ls-terracotta-dark)] transition-colors"
              data-testid="back-to-dashboard">
              {t('Retour au tableau de bord', 'Back to dashboard')}
            </button>
          </div>
        </div>
      </OrbitLayout>
    );
  }

  // Confirmation step (after submission)
  if (step === 5) {
    return (
      <OrbitLayout>
        <div className="max-w-2xl mx-auto" data-testid="business-confirmed">
          <div className="bg-white border border-[hsl(40,20%,88%)] rounded-2xl p-8 text-center">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-emerald-400" weight="fill" />
            </div>
            <h2 className="text-2xl font-bold text-[var(--ls-text)] mb-3">
              {t('Inscription soumise !', 'Registration submitted!')}
            </h2>
            <p className="text-[var(--ls-text-muted)] mb-2 max-w-md mx-auto">
              {t(
                'Votre entreprise a ete soumise pour approbation. Vous serez notifie une fois la revue terminee.',
                'Your business has been submitted for approval. You will be notified once the review is complete.'
              )}
            </p>
            <p className="text-sm text-[var(--ls-terracotta)] mb-8">
              {t('Nom : ', 'Name: ')}<strong>{form.name}</strong>
              {' — '}{t('Plan : ', 'Plan: ')}<strong className="capitalize">{form.plan}</strong>
            </p>
            <button onClick={() => navigate('/dashboard')}
              className="px-8 py-3 bg-[var(--ls-terracotta)] text-white rounded-xl font-semibold hover:bg-[var(--ls-terracotta-dark)] transition-colors"
              data-testid="go-to-dashboard">
              {t('Retour au tableau de bord', 'Back to dashboard')}
            </button>
          </div>
        </div>
      </OrbitLayout>
    );
  }

  const STEPS = [
    { num: 1, label_fr: 'Entreprise', label_en: 'Business', icon: Storefront },
    { num: 2, label_fr: 'Localisation', label_en: 'Location', icon: MapPin },
    { num: 3, label_fr: 'Plan', label_en: 'Plan', icon: CurrencyDollar },
    { num: 4, label_fr: 'Confirmation', label_en: 'Confirm', icon: Check },
  ];

  return (
    <OrbitLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="business-register-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[var(--ls-text)] flex items-center gap-2" data-testid="biz-register-title">
            <Storefront className="w-7 h-7 text-[var(--ls-terracotta)]" weight="fill" />
            {t('Inscrire mon entreprise', 'Register my business')}
          </h1>
          <p className="text-[var(--ls-text-muted)] text-sm mt-1">
            {t('Ajoutez votre entreprise au repertoire LocalSite.Club', 'Add your business to the LocalSite.Club directory')}
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-1" data-testid="step-indicator">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.num}>
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${
                step === s.num ? 'bg-[var(--ls-terracotta)] text-white' : step > s.num ? 'bg-[var(--ls-success)]/15 text-[var(--ls-success)]' : 'bg-[var(--ls-sand-dark)] text-[var(--ls-text-muted)]'
              }`}>
                {step > s.num ? <CheckCircle className="w-4 h-4" weight="fill" /> : <s.icon className="w-4 h-4" />}
                <span className="hidden sm:inline">{language === 'fr' ? s.label_fr : s.label_en}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${step > s.num ? 'bg-[var(--ls-success)]/30' : 'bg-[var(--ls-sand-dark)]'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Form content */}
        <div className="bg-white border border-[hsl(40,20%,88%)] rounded-2xl p-6">
          {/* STEP 1: Business Info */}
          {step === 1 && (
            <div className="space-y-5" data-testid="step-1-business">
              <div>
                <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">
                  {t('Nom de l\'entreprise', 'Business name')} *
                </label>
                <input type="text" value={form.name} onChange={e => updateForm('name', e.target.value)}
                  placeholder={t('Ex: Boulangerie Le Pain Dore', 'Ex: The Golden Bread Bakery')}
                  className="w-full px-4 py-3 bg-white border border-[hsl(40,20%,88%)] rounded-xl text-[var(--ls-text)] placeholder-[var(--ls-text-muted)] focus:outline-none focus:border-[var(--ls-terracotta)]"
                  data-testid="biz-name-input" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">
                    <Tag className="w-4 h-4 inline -mt-0.5 mr-1 text-[var(--ls-terracotta)]" />
                    {t('Categorie', 'Category')} *
                  </label>
                  <select value={form.category} onChange={e => { updateForm('category', e.target.value); updateForm('subcategory', ''); }}
                    className="w-full px-4 py-3 bg-white border border-[hsl(40,20%,88%)] rounded-xl text-[var(--ls-text)] focus:outline-none focus:border-[var(--ls-terracotta)] appearance-none"
                    data-testid="biz-category-select">
                    <option value="">{t('Choisir...', 'Choose...')}</option>
                    {categories.map(c => (
                      <option key={c.key} value={c.key}>{language === 'fr' ? c.name_fr : c.name_en}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">
                    {t('Sous-categorie', 'Subcategory')}
                  </label>
                  <select value={form.subcategory} onChange={e => updateForm('subcategory', e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-[hsl(40,20%,88%)] rounded-xl text-[var(--ls-text)] focus:outline-none focus:border-[var(--ls-terracotta)] appearance-none"
                    data-testid="biz-subcategory-select">
                    <option value="">{t('Choisir...', 'Choose...')}</option>
                    {filteredSubs.map(s => (
                      <option key={s.key} value={s.key}>{language === 'fr' ? s.name_fr : s.name_en}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">
                  {t('Description (Francais)', 'Description (French)')} *
                </label>
                <textarea value={form.description_fr} onChange={e => updateForm('description_fr', e.target.value)} rows={3}
                  placeholder={t('Decrivez votre entreprise en francais...', 'Describe your business in French...')}
                  className="w-full px-4 py-3 bg-white border border-[hsl(40,20%,88%)] rounded-xl text-[var(--ls-text)] placeholder-[var(--ls-text-muted)] focus:outline-none focus:border-[var(--ls-terracotta)] resize-none"
                  data-testid="biz-desc-fr" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">
                  {t('Description (Anglais)', 'Description (English)')} *
                </label>
                <textarea value={form.description_en} onChange={e => updateForm('description_en', e.target.value)} rows={3}
                  placeholder={t('Decrivez votre entreprise en anglais...', 'Describe your business in English...')}
                  className="w-full px-4 py-3 bg-white border border-[hsl(40,20%,88%)] rounded-xl text-[var(--ls-text)] placeholder-[var(--ls-text-muted)] focus:outline-none focus:border-[var(--ls-terracotta)] resize-none"
                  data-testid="biz-desc-en" />
              </div>
            </div>
          )}

          {/* STEP 2: Location */}
          {step === 2 && (
            <div className="space-y-5" data-testid="step-2-location">
              <div>
                <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">
                  <MapPin className="w-4 h-4 inline -mt-0.5 mr-1 text-[var(--ls-terracotta)]" />
                  {t('Code postal', 'Postal / ZIP code')} *
                </label>
                <div className="relative">
                  <input type="text" value={form.postal_code} onChange={handlePostalChange}
                    placeholder={t('Ex: H2X 1Y4', 'Ex: H2X 1Y4')}
                    className="w-full px-4 py-3 bg-white border border-[hsl(40,20%,88%)] rounded-xl text-[var(--ls-text)] placeholder-[var(--ls-text-muted)] focus:outline-none focus:border-[var(--ls-terracotta)] pr-10"
                    data-testid="biz-postal-input" />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {locLoading && <SpinnerGap className="w-5 h-5 text-[var(--ls-terracotta)] animate-spin" />}
                    {locDetected && !locLoading && <CheckCircle className="w-5 h-5 text-emerald-400" weight="fill" />}
                  </div>
                </div>
              </div>

              {locDetected && (
                <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-4 space-y-3" data-testid="biz-location-detected">
                  <div className="flex items-center gap-2 text-sm text-indigo-300">
                    <MapPin className="w-4 h-4 text-[var(--ls-terracotta)]" weight="fill" />
                    {t('Localisation detectee', 'Location detected')}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <span className="block text-xs text-[var(--ls-text-muted)] mb-1">{t('Pays', 'Country')}</span>
                      <span className="text-sm text-[var(--ls-text)] font-medium">{form.country === 'CA' ? 'Canada' : 'USA'}</span>
                    </div>
                    <div>
                      <span className="block text-xs text-[var(--ls-text-muted)] mb-1">{t('Province', 'Province')}</span>
                      <span className="text-sm text-[var(--ls-text)] font-medium">{form.province_code}</span>
                    </div>
                    <div>
                      <span className="block text-xs text-[var(--ls-text-muted)] mb-1">{t('Ville', 'City')}</span>
                      <span className="text-sm text-[var(--ls-text)] font-medium">{form.city}</span>
                    </div>
                  </div>
                  {form.neighborhood && (
                    <div>
                      <span className="block text-xs text-[var(--ls-text-muted)] mb-1">{t('Quartier', 'Neighborhood')}</span>
                      <span className="text-sm text-indigo-200">{form.neighborhood}</span>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">
                  {t('Adresse complete', 'Full address')}
                </label>
                <input type="text" value={form.address} onChange={e => updateForm('address', e.target.value)}
                  placeholder={t('Ex: 1234 Rue Saint-Denis, Montreal, QC', 'Ex: 1234 Saint-Denis Street, Montreal, QC')}
                  className="w-full px-4 py-3 bg-white border border-[hsl(40,20%,88%)] rounded-xl text-[var(--ls-text)] placeholder-[var(--ls-text-muted)] focus:outline-none focus:border-[var(--ls-terracotta)]"
                  data-testid="biz-address-input" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">
                    <PhoneIcon className="w-4 h-4 inline -mt-0.5 mr-1 text-[var(--ls-terracotta)]" />
                    {t('Telephone', 'Phone')}
                  </label>
                  <input type="tel" value={form.phone} onChange={e => updateForm('phone', e.target.value)}
                    placeholder="+1 (514) 555-0000"
                    className="w-full px-4 py-3 bg-white border border-[hsl(40,20%,88%)] rounded-xl text-[var(--ls-text)] placeholder-[var(--ls-text-muted)] focus:outline-none focus:border-[var(--ls-terracotta)]"
                    data-testid="biz-phone-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">
                    <Envelope className="w-4 h-4 inline -mt-0.5 mr-1 text-[var(--ls-terracotta)]" />
                    {t('Courriel', 'Email')}
                  </label>
                  <input type="email" value={form.email} onChange={e => updateForm('email', e.target.value)}
                    placeholder="info@votre.entreprise"
                    className="w-full px-4 py-3 bg-white border border-[hsl(40,20%,88%)] rounded-xl text-[var(--ls-text)] placeholder-[var(--ls-text-muted)] focus:outline-none focus:border-[var(--ls-terracotta)]"
                    data-testid="biz-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--ls-text)] mb-1.5">
                    <Globe className="w-4 h-4 inline -mt-0.5 mr-1 text-[var(--ls-terracotta)]" />
                    {t('Site web', 'Website')}
                  </label>
                  <input type="url" value={form.website} onChange={e => updateForm('website', e.target.value)}
                    placeholder="https://..."
                    className="w-full px-4 py-3 bg-white border border-[hsl(40,20%,88%)] rounded-xl text-[var(--ls-text)] placeholder-[var(--ls-text-muted)] focus:outline-none focus:border-[var(--ls-terracotta)]"
                    data-testid="biz-website-input" />
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Plan selection */}
          {step === 3 && (
            <div className="space-y-4" data-testid="step-3-plan">
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-4 h-4 text-[var(--ls-terracotta)]" />
                <p className="text-sm text-[var(--ls-text-muted)]">
                  {t('Le paiement sera configure une fois votre fiche approuvee par l\'admin.',
                     'Payment will be configured once your listing is approved by the admin.')}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {PLANS.map((plan) => (
                  <button key={plan.key} onClick={() => updateForm('plan', plan.key)}
                    className={`relative text-left p-5 rounded-xl border-2 transition-all ${
                      form.plan === plan.key
                        ? 'border-indigo-500 bg-indigo-500/10'
                        : 'border-indigo-500/10 bg-[var(--ls-sand-dark)] hover:border-indigo-500/30'
                    }`}
                    data-testid={`plan-${plan.key}`}>
                    {plan.popular && (
                      <span className="absolute -top-2.5 right-3 bg-[var(--ls-terracotta)] text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full">
                        {t('Populaire', 'Popular')}
                      </span>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[var(--ls-text)] font-bold">{language === 'fr' ? plan.name_fr : plan.name_en}</span>
                      <span className="text-xl font-bold text-[var(--ls-text)]">{plan.price}<span className="text-xs text-[var(--ls-text-muted)] font-normal">/mo</span></span>
                    </div>
                    <p className="text-xs text-[var(--ls-text-muted)] mb-3">{language === 'fr' ? plan.desc_fr : plan.desc_en}</p>
                    <ul className="space-y-1.5">
                      {(language === 'fr' ? plan.features_fr : plan.features_en).map((f, i) => (
                        <li key={i} className="flex items-center gap-1.5 text-xs text-[var(--ls-text)]">
                          <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" weight="bold" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    {form.plan === plan.key && (
                      <div className="absolute top-3 left-3">
                        <CheckCircle className="w-5 h-5 text-[var(--ls-terracotta)]" weight="fill" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 4: Confirmation */}
          {step === 4 && (
            <div className="space-y-5" data-testid="step-4-confirm">
              <h3 className="text-lg font-bold text-[var(--ls-text)]">{t('Revue de votre inscription', 'Review your registration')}</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <SummaryItem label={t('Nom', 'Name')} value={form.name} />
                <SummaryItem label={t('Categorie', 'Category')}
                  value={language === 'fr'
                    ? (categories.find(c => c.key === form.category)?.name_fr || form.category)
                    : (categories.find(c => c.key === form.category)?.name_en || form.category)} />
                {form.subcategory && (
                  <SummaryItem label={t('Sous-categorie', 'Subcategory')}
                    value={language === 'fr'
                      ? (subcategories.find(s => s.key === form.subcategory)?.name_fr || form.subcategory)
                      : (subcategories.find(s => s.key === form.subcategory)?.name_en || form.subcategory)} />
                )}
                <SummaryItem label={t('Ville', 'City')} value={`${form.city}${form.neighborhood ? ` (${form.neighborhood})` : ''}`} />
                <SummaryItem label={t('Province', 'Province')} value={form.province_code} />
                <SummaryItem label={t('Code postal', 'Postal code')} value={form.postal_code} />
                {form.address && <SummaryItem label={t('Adresse', 'Address')} value={form.address} />}
                {form.phone && <SummaryItem label={t('Telephone', 'Phone')} value={form.phone} />}
                {form.email && <SummaryItem label={t('Courriel', 'Email')} value={form.email} />}
                {form.website && <SummaryItem label={t('Site web', 'Website')} value={form.website} />}
                <SummaryItem label={t('Plan choisi', 'Chosen plan')}
                  value={`${language === 'fr' ? PLANS.find(p => p.key === form.plan)?.name_fr : PLANS.find(p => p.key === form.plan)?.name_en} (${PLANS.find(p => p.key === form.plan)?.price}/mo)`} />
              </div>

              <div className="bg-[var(--ls-sand-dark)] border border-indigo-500/10 rounded-xl p-4">
                <h4 className="text-xs font-semibold text-[var(--ls-text-muted)] uppercase mb-2">{t('Description FR', 'Description FR')}</h4>
                <p className="text-sm text-[var(--ls-text)]">{form.description_fr}</p>
              </div>
              <div className="bg-[var(--ls-sand-dark)] border border-indigo-500/10 rounded-xl p-4">
                <h4 className="text-xs font-semibold text-[var(--ls-text-muted)] uppercase mb-2">{t('Description EN', 'Description EN')}</h4>
                <p className="text-sm text-[var(--ls-text)]">{form.description_en}</p>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-indigo-500/10">
            <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-[var(--ls-text-muted)] hover:text-[var(--ls-text)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="step-prev">
              <ArrowLeft className="w-4 h-4" />
              {t('Retour', 'Back')}
            </button>

            {step < 4 ? (
              <button onClick={() => setStep(step + 1)} disabled={!canGoNext()}
                className="flex items-center gap-2 px-6 py-2.5 bg-[var(--ls-terracotta)] text-white rounded-xl text-sm font-semibold hover:bg-[var(--ls-terracotta-dark)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="step-next">
                {t('Suivant', 'Next')}
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading}
                className="flex items-center gap-2 px-8 py-3 bg-emerald-600 text-[var(--ls-text)] rounded-xl text-sm font-bold hover:bg-emerald-500 disabled:opacity-50 transition-colors"
                data-testid="submit-business">
                {loading ? <SpinnerGap className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" weight="fill" />}
                {loading ? t('Soumission...', 'Submitting...') : t('Soumettre mon entreprise', 'Submit my business')}
              </button>
            )}
          </div>
        </div>
      </div>
    </OrbitLayout>
  );
}

function SummaryItem({ label, value }) {
  if (!value) return null;
  return (
    <div className="bg-[var(--ls-sand-dark)] rounded-xl p-3">
      <span className="block text-xs text-[var(--ls-text-muted)] mb-0.5">{label}</span>
      <span className="text-sm text-[var(--ls-text)] font-medium">{value}</span>
    </div>
  );
}
